"use client";

import type { Voice } from "@/app/_data/voices";
import type { FC } from "react";

import { useState } from "react";
import clsx from "clsx";

import { BookNavigationArrows } from "./BookNavigationArrows";
import { TranscriptionContent } from "./TranscriptionContent";
import { LimitReachedOverlay } from "../LimitReachedOverlay";
import { TTSModels } from "@mw/shared/models";

export interface TranscriptionCardProps {
  /** The text to be converted to speech */
  text: string;
  /** Label displayed above the chapter title (e.g., "Chapter 1") */
  chapterLabel?: string;
  /** Chapter title displayed below the label */
  chapterTitle?: string;
  /** Book title (shown on mobile instead of chapter title) */
  bookTitle?: string;
  /** Author name (shown on mobile instead of chapter label) */
  author?: string;
  /** Available voices for selection */
  voices: Voice[];
  /** Currently selected voice */
  selectedVoice?: Voice;
  /** Callback when voice changes */
  onVoiceChange: (voice: Voice) => void;
  /** TTS model to use (defaults to ELEVEN_TURBO_V2_5) */
  model?: TTSModels;
  /** Callback for previous book navigation (shown on mobile) */
  onPrev?: () => void;
  /** Callback for next book navigation (shown on mobile) */
  onNext?: () => void;
}

export const TranscriptionCard: FC<TranscriptionCardProps> = ({
  text,
  chapterLabel,
  chapterTitle,
  bookTitle,
  author,
  voices,
  selectedVoice,
  onVoiceChange,
  model = TTSModels.ELEVEN_TURBO_V2_5,
  onPrev,
  onNext,
}) => {
  const [showLimitReachedOverlay, setShowLimitReachedOverlay] = useState(false);

  return (
    <div
      className={clsx(
        "tw-relative tw-h-full tw-rounded-2xl tw-overflow-hidden",
        "tw-bg-cream tw-ring-1 tw-ring-gray-200",
        "tw-flex tw-flex-col",
        "tw-px-6 md:tw-px-24 tw-pt-6 md:tw-pt-12",
      )}
    >
      {/* Limit reached overlay */}
      {showLimitReachedOverlay && (
        <div className='tw-absolute tw-inset-0 tw-z-20 tw-flex tw-items-center tw-justify-center tw-bg-cream tw-rounded-2xl'>
          <LimitReachedOverlay variant='redesigned' onClose={() => setShowLimitReachedOverlay(false)} />
        </div>
      )}

      {/* Navigation arrows - single column only (below lg), top right */}
      {!showLimitReachedOverlay && (
        <BookNavigationArrows
          onPrev={onPrev}
          onNext={onNext}
          variant='elevated'
          size='responsive'
          className='tw-absolute tw-top-4 tw-right-4 tw-z-20 lg:tw-hidden'
        />
      )}

      <div className='tw-pt-8 sm:tw-pt-0 tw-flex-1 tw-flex tw-flex-col tw-min-h-0'>
        <TranscriptionContent
          text={text}
          chapterLabel={chapterLabel}
          chapterTitle={chapterTitle}
          bookTitle={bookTitle}
          author={author}
          voices={voices}
          selectedVoice={selectedVoice}
          onVoiceChange={onVoiceChange}
          model={model}
          gradientFromColor='tw-from-cream'
          gradientViaColor='tw-via-cream/80'
          voiceSelectorWidthClassName='tw-w-40 sm:tw-w-56'
          onQuotaExceeded={() => setShowLimitReachedOverlay(true)}
        />
      </div>
    </div>
  );
};
